import React, { useCallback, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { useNavigate } from "react-router-dom";
import api from "./api";
import {
  emptyChatState,
  formatSeenAt,
  getOtherUser,
  normalizeLastLogins,
  normalizeMessages,
  normalizeSeen,
  normalizeSeenAt,
  normalizeUsername,
} from "./chatUtils";
import { hasMarkedCurrentMessage, shouldShowSeenButton } from "./seenUtils";

const SOCKET_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const TYPING_DEBOUNCE_MS = 150;

function UserBadge({ name }) {
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-700 text-sm font-bold text-slate-200 sm:h-11 sm:w-11">
      {name}
    </div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex gap-0.5 pl-1" aria-hidden="true">
      <span className="h-1 w-1 animate-bounce rounded-full bg-indigo-300 [animation-delay:0ms]" />
      <span className="h-1 w-1 animate-bounce rounded-full bg-indigo-300 [animation-delay:150ms]" />
      <span className="h-1 w-1 animate-bounce rounded-full bg-indigo-300 [animation-delay:300ms]" />
    </span>
  );
}

function OtherMessageCard({ author, text, isTypingActive }) {
  const hasMessage = Boolean(text);

  return (
    <div className="flex gap-3">
      <UserBadge name={author} />
      <div className="max-w-[85%] flex-1 sm:max-w-[80%]">
        <div className="rounded-2xl rounded-tl-md border border-slate-700 bg-slate-800 px-4 py-3 text-slate-100">
          <p className="mb-2 flex items-center text-[11px] font-semibold uppercase tracking-wide text-slate-400 sm:text-xs">
            User {author}
            {isTypingActive && hasMessage && (
              <span className="ml-2 normal-case tracking-normal text-indigo-300">
                is typing
                <TypingDots />
              </span>
            )}
          </p>

          {!hasMessage && (
            <p className="text-sm italic text-slate-500">No message yet...</p>
          )}

          {hasMessage && (
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
              {text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Chat() {
  const defaults = emptyChatState();

  const [messages, setMessages] = useState(() => {
    const saved = sessionStorage.getItem("messages");
    return saved ? normalizeMessages(saved) : defaults.messages;
  });

  const [seen, setSeen] = useState(() => {
    const saved = sessionStorage.getItem("seen");
    return saved ? normalizeSeen(saved) : defaults.seen;
  });

  const [seenAt, setSeenAt] = useState(() => {
    const saved = sessionStorage.getItem("seenAt");
    return saved ? normalizeSeenAt(saved) : defaults.seenAt;
  });

  const [lastLogins, setLastLogins] = useState(() => {
    const saved = sessionStorage.getItem("lastLogins");
    return saved ? normalizeLastLogins(saved) : defaults.lastLogins;
  });

  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const [otherTypingActive, setOtherTypingActive] = useState(false);
  const username = normalizeUsername(sessionStorage.getItem("username"));
  const otherUser = username ? getOtherUser(username) : null;
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const debounceRef = useRef(null);
  const lastOtherMessageRef = useRef("");
  const lastOwnMessageRef = useRef("");
  const otherTypingTimeoutRef = useRef(null);
  const [seenAtMessage, setSeenAtMessage] = useState("");
  const messagesRef = useRef(messages);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const persistSeen = useCallback((updater) => {
    setSeen((prev) => {
      const next = normalizeSeen(typeof updater === "function" ? updater(prev) : updater);
      sessionStorage.setItem("seen", JSON.stringify(next));
      return next;
    });
  }, []);

  const persistSeenAt = useCallback((updater) => {
    setSeenAt((prev) => {
      const next = normalizeSeenAt(
        typeof updater === "function" ? updater(prev) : updater
      );
      sessionStorage.setItem("seenAt", JSON.stringify(next));
      return next;
    });
  }, []);

  const persistLastLogins = useCallback((updater) => {
    setLastLogins((prev) => {
      const next = normalizeLastLogins(
        typeof updater === "function" ? updater(prev) : updater
      );
      sessionStorage.setItem("lastLogins", JSON.stringify(next));
      return next;
    });
  }, []);

  const persistMessages = useCallback((updater) => {
    setMessages((prev) => {
      const next = normalizeMessages(
        typeof updater === "function" ? updater(prev) : updater
      );
      sessionStorage.setItem("messages", JSON.stringify(next));
      return next;
    });
  }, []);

  const handleMarkSeen = useCallback(() => {
    if (!otherUser || !messages[otherUser] || !socketRef.current) {
      return;
    }

    const currentMessage = messages[otherUser];
    const now = new Date().toISOString();
    socketRef.current.emit("markSeen", { author: otherUser });
    setSeenAtMessage(currentMessage);
    persistSeen((prev) => ({ ...prev, [otherUser]: username }));
    persistSeenAt((prev) => ({ ...prev, [otherUser]: now }));
  }, [messages, otherUser, username, persistSeen, persistSeenAt]);

  const handleLogout = useCallback(async () => {
    clearTimeout(debounceRef.current);
    const token = sessionStorage.getItem("token");

    socketRef.current?.disconnect();
    socketRef.current = null;

    if (token) {
      try {
        await api.post(
          "/logout",
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch {
        // Session may already be invalid; still clear local state.
      }
    }

    sessionStorage.clear();
    navigate("/");
  }, [navigate]);

  useEffect(() => {
    if (!otherUser) return;

    const otherMessage = messages[otherUser] || "";
    if (otherMessage !== lastOtherMessageRef.current) {
      lastOtherMessageRef.current = otherMessage;
      setOtherTypingActive(true);
      clearTimeout(otherTypingTimeoutRef.current);
      otherTypingTimeoutRef.current = setTimeout(() => {
        setOtherTypingActive(false);
      }, 1200);

      if (otherMessage !== seenAtMessage) {
        persistSeen((prev) => {
          if (prev[otherUser] === null) return prev;
          return { ...prev, [otherUser]: null };
        });
        persistSeenAt((prev) => {
          if (prev[otherUser] === null) return prev;
          return { ...prev, [otherUser]: null };
        });
        setSeenAtMessage("");
      }
    }
  }, [messages, otherUser, seenAtMessage, persistSeen, persistSeenAt]);

  useEffect(() => {
    if (!username) return;

    const ownMessage = messages[username] || "";
    if (ownMessage !== lastOwnMessageRef.current) {
      lastOwnMessageRef.current = ownMessage;
      persistSeen((prev) => {
        if (prev[username] === null) return prev;
        return { ...prev, [username]: null };
      });
      persistSeenAt((prev) => {
        if (prev[username] === null) return prev;
        return { ...prev, [username]: null };
      });
    }
  }, [messages, username, persistSeen, persistSeenAt]);

  useEffect(() => {
    return () => clearTimeout(otherTypingTimeoutRef.current);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!username || !otherUser || !token) {
      navigate("/");
      return;
    }

    const parsedMessages = normalizeMessages(sessionStorage.getItem("messages"));
    const parsedSeen = normalizeSeen(sessionStorage.getItem("seen"));
    const parsedSeenAt = normalizeSeenAt(sessionStorage.getItem("seenAt"));
    setMessages(parsedMessages);
    lastOtherMessageRef.current = parsedMessages[otherUser] || "";
    lastOwnMessageRef.current = parsedMessages[username] || "";

    if (parsedSeen[otherUser] === username && parsedMessages[otherUser]) {
      setSeenAtMessage(parsedMessages[otherUser]);
    } else {
      setSeenAtMessage("");
    }

    setSeen(parsedSeen);
    setSeenAt(parsedSeenAt);
    setLastLogins(normalizeLastLogins(sessionStorage.getItem("lastLogins")));

    const socket = io(SOCKET_URL, { auth: { token } });
    socketRef.current = socket;

    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));

    socket.on("updateChat", ({ messages: msgs, seen: seenState, seenAt: seenAtState, lastLogins: logins }) => {
      const normalizedMessages = normalizeMessages(msgs);
      setMessages(normalizedMessages);
      persistSeen(seenState);
      if (seenAtState) {
        persistSeenAt(seenAtState);
      }
      if (logins) {
        persistLastLogins(logins);
      }
      sessionStorage.setItem("messages", JSON.stringify(normalizedMessages));
    });

    socket.on("messageUpdated", ({ username: author, text: updatedText }) => {
      persistMessages((prev) => ({ ...prev, [author]: updatedText }));

      if (author === username) {
        persistSeen((prev) => ({ ...prev, [author]: null }));
        persistSeenAt((prev) => ({ ...prev, [author]: null }));
        return;
      }

      persistSeen((prev) => ({ ...prev, [author]: null }));
      persistSeenAt((prev) => ({ ...prev, [author]: null }));
      if (author === otherUser) {
        setSeenAtMessage("");
      }
    });

    socket.on("seenUpdated", ({ author, seenBy, seenAt: seenAtTime }) => {
      persistSeen((prev) => ({ ...prev, [author]: seenBy }));
      persistSeenAt((prev) => ({ ...prev, [author]: seenAtTime ?? null }));
      if (author === otherUser) {
        if (seenBy === username) {
          setSeenAtMessage(messagesRef.current[otherUser] || "");
        } else if (seenBy === null) {
          setSeenAtMessage("");
        }
      }
    });

    socket.on("lastLoginsUpdated", ({ lastLogins: logins }) => {
      if (logins) {
        persistLastLogins(logins);
      }
    });

    socket.on("connect_error", () => {
      sessionStorage.clear();
      navigate("/");
    });

    const handleVisibility = () => {
      if (document.hidden) {
        handleLogout();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearTimeout(debounceRef.current);
      socket.disconnect();
      socketRef.current = null;
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [
    username,
    otherUser,
    navigate,
    handleLogout,
    persistSeen,
    persistSeenAt,
    persistLastLogins,
    persistMessages,
  ]);

  const handleChange = (e) => {
    const val = e.target.value;
    setText(val);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      persistMessages((prev) => ({ ...prev, [username]: val }));
      socketRef.current?.emit("typing", { text: val });
    }, TYPING_DEBOUNCE_MS);
  };

  const clearMessage = () => {
    if (!username) return;

    clearTimeout(debounceRef.current);
    setText("");
    persistMessages((prev) => ({ ...prev, [username]: "" }));
    persistSeen((prev) => ({ ...prev, [username]: null }));
    persistSeenAt((prev) => ({ ...prev, [username]: null }));
    lastOwnMessageRef.current = "";
    socketRef.current?.emit("clearMessage");
  };

  if (!username || !otherUser) {
    return null;
  }

  const otherMessage = messages[otherUser] || "";
  const iMarkedOtherSeen = hasMarkedCurrentMessage({
    otherMessage,
    seen,
    username,
    otherUser,
    seenAtMessage,
  });
  const showSeenButton = shouldShowSeenButton({
    otherMessage,
    seen,
    username,
    otherUser,
    seenAtMessage,
  });
  const ownMessage = (text || messages[username] || "").trim();
  const ownMessageSeenByOther =
    Boolean(ownMessage) && seen[username] === otherUser;
  const ownMessageSeenAt = ownMessageSeenByOther ? formatSeenAt(seenAt[username]) : null;
  const otherLastLogin = lastLogins[otherUser];

  return (
    <div className="app-shell">
      <header className="shrink-0 border-b border-slate-800 bg-slate-900/90 backdrop-blur-xl">
        <div className="flex items-center justify-between gap-3 px-4 py-3 sm:mx-auto sm:max-w-4xl sm:px-6 sm:py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-lg font-bold text-white sm:text-xl">
                Chat with {otherUser}
              </h1>
              <span
                className={`inline-flex h-2 w-2 shrink-0 rounded-full ${
                  connected ? "bg-emerald-400" : "bg-amber-400"
                }`}
                aria-label={connected ? "Connected" : "Connecting"}
              />
            </div>
            <p className="mt-1 text-xs text-slate-400 sm:text-sm">
              {otherLastLogin ? (
                <>
                  Last login{" "}
                  <span className="font-medium text-emerald-300">
                    {otherLastLogin}
                  </span>
                </>
              ) : (
                <span className="text-slate-500">
                  User {otherUser} has not logged in yet
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="btn-secondary shrink-0 px-3"
          >
            Out
          </button>
        </div>
      </header>

      <main className="flex min-h-0 flex-1 flex-col sm:mx-auto sm:w-full sm:max-w-4xl">
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-6 sm:py-6">
          <OtherMessageCard
            author={otherUser}
            text={otherMessage}
            isTypingActive={otherTypingActive}
          />
        </div>

        <div className="shrink-0 border-t border-slate-800 bg-slate-900/95 px-3 py-3 backdrop-blur-xl sm:px-6 sm:py-4">
          <label
            htmlFor="message"
            className="mb-2 block text-sm font-medium text-slate-300"
          >
            Write a new message
          </label>
          {ownMessageSeenByOther && (
            <p className="mb-2 text-sm font-medium text-emerald-300">
              Seen by {otherUser}
              {ownMessageSeenAt ? ` · ${ownMessageSeenAt}` : " ✓✓"}
            </p>
          )}
          <textarea
            id="message"
            value={text}
            onChange={handleChange}
            placeholder="Type here..."
            rows={3}
            className="input-field resize-none"
          />
          <div className="mt-3 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={clearMessage}
              className="btn-secondary shrink-0 sm:w-auto"
            >
              Clear
            </button>
            {showSeenButton && (
              <button
                type="button"
                onClick={handleMarkSeen}
                className="min-h-[44px] rounded-xl border border-indigo-400/40 bg-indigo-500/20 px-4 py-2.5 text-sm font-semibold text-indigo-200 transition active:scale-[0.98] hover:bg-indigo-500/30"
              >
                Seen
              </button>
            )}
            {!showSeenButton && iMarkedOtherSeen && (
              <span className="text-sm font-medium text-emerald-300">Seen ✓</span>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
